'use client';

import clsx from 'clsx';
import { useContext, type ForwardedRef } from 'react';
import { useId } from 'react-aria';
import {
  LabelContext as RACLabelContext,
  NumberField as RACNumberField,
  Provider as RACProvider,
  Slider as RACSlider,
  SliderOutput as RACSliderOutput,
  SliderStateContext as RACSliderStateContext,
  SliderThumb as RACSliderThumb,
  SliderTrack as RACSliderTrack,
  TextContext as RACTextContext,
  useSlottedContext,
  type NumberFieldProps as RACNumberFieldProps,
  type SliderProps as RACSliderProps,
} from 'react-aria-components';
import { forwardRef } from '../../forward-ref.js';
import { useLocalizedMessages } from '../../i18n/localization-provider.js';
import { useProductionWarning } from '../../utils/use-production-warning.js';
import { withStyleProps } from '../../with-style-props.js';
import { FormFieldDescription, FormFieldLabel } from '../internal/form-field/form-field.js';
import { Input } from '../internal/input/input.js';
import type { CommonProps, LabellableProps } from '../types.js';
import { Text } from '../typography/text.js';

export interface SliderProps<T extends number | [number, number] = number>
  extends
    CommonProps,
    LabellableProps,
    Pick<RACSliderProps<T>, 'isDisabled' | 'minValue' | 'maxValue' | 'step' | 'formatOptions'> {
  /** A description for the field. Provides a hint such as specific requirements for what to choose. */
  description?: string;
  /** The current value (controlled). */
  value?: T;
  /** The default value (uncontrolled). */
  defaultValue?: T;
  /** Handler that is called when the value changes. */
  onChange?: (value: T) => void;
  /** Handler that is called when the slider stops moving. */
  onChangeEnd?: (value: T) => void;
  /**
   * The `<form>` element to associate the input with.
   * The value of this attribute must be the id of a `<form>` in the same document.
   * See [MDN](https://developer.mozilla.org/en-US/docs/Web/HTML/Reference/Elements/input#form).
   */
  form?: string;
  /**
   * The name(s) of the input element(s), used when submitting an HTML form. See [MDN](https://developer.mozilla.org/en-US/docs/Web/HTML/Reference/Elements/input#name).
   */
  name?: T extends [number, number] ? [string, string] : string;
  /**
   * Whether to show input fields for slider values.
   * @default false
   */
  withInput?: boolean;
  /**
   * Whether to show static labels with minimum and maximum values.
   * @default false
   * */
  showLabels?: boolean;
}

function Slider<T extends number | [number, number] = number>(
  {
    label,
    description,
    UNSAFE_className,
    UNSAFE_style,
    form,
    name,
    withInput,
    showLabels,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    ...props
  }: SliderProps<T>,
  ref: ForwardedRef<HTMLDivElement>,
) {
  const descriptionId = useId();
  const rangeStartLabelId = useId();
  const rangeEndLabelId = useId();
  const messages = useLocalizedMessages('slider');

  useProductionWarning(() => {
    if (!label && !ariaLabel && !ariaLabelledBy) {
      console.warn('Slider requires one of label / aria-label / aria-labelledby for accessibility');
    }
  }, [label, ariaLabel, ariaLabelledBy]);

  return (
    <RACProvider values={[[RACTextContext, { slots: { description: { id: descriptionId } } }]]}>
      <RACSlider
        {...props}
        ref={ref}
        className={clsx('cim-slider', UNSAFE_className)}
        style={UNSAFE_style}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        orientation="horizontal"
        aria-describedby={[description && descriptionId, props['aria-describedby']].filter(Boolean).join(' ')}
      >
        {({ state, isDisabled }) => {
          const isRangeSlider = state.values.length === 2;

          return (
            <>
              <FormFieldLabel isRequired={false} isDisabled={isDisabled}>
                {label}
              </FormFieldLabel>

              {!withInput && label != null && (
                <RACSliderOutput className="cim-slider-output">
                  <Text as="span" variant="medium">
                    {state.values.map((_, i) => state.getThumbValueLabel(i)).join(' – ')}
                  </Text>
                </RACSliderOutput>
              )}

              <div className="cim-slider-track-container">
                {showLabels && (
                  <Text as="span" variant="medium" UNSAFE_className="cim-slider-label-min">
                    {state.getThumbMinValue(0)}
                  </Text>
                )}

                <RACSliderTrack className="cim-slider-track">
                  {({ state: trackState, isDisabled: trackIsDisabled }) => (
                    <>
                      <div className="cim-slider-track-background" data-disabled={trackIsDisabled || undefined} />

                      <div
                        className="cim-slider-fill"
                        style={
                          (state.values.length === 1
                            ? {
                                '--cim-slider-fill-size': trackState.getThumbPercent(0) * 100 + '%',
                              }
                            : isRangeSlider
                              ? {
                                  '--cim-slider-fill-start': trackState.getThumbPercent(0) * 100 + '%',
                                  '--cim-slider-fill-size':
                                    (trackState.getThumbPercent(1) - trackState.getThumbPercent(0)) * 100 + '%',
                                }
                              : {}) as React.CSSProperties
                        }
                      />

                      {trackState.values.map((_, i) => (
                        <RACSliderThumb
                          key={i}
                          index={i}
                          className="cim-slider-thumb"
                          name={typeof name === 'string' ? name : name?.[i]}
                          form={form}
                          aria-labelledby={isRangeSlider ? (i === 0 ? rangeStartLabelId : rangeEndLabelId) : undefined}
                        />
                      ))}
                    </>
                  )}
                </RACSliderTrack>

                {showLabels && (
                  <Text as="span" variant="medium" UNSAFE_className="cim-slider-label-max">
                    {state.getThumbMaxValue(state.values.length - 1)}
                  </Text>
                )}

                {withInput && (
                  <div className="cim-slider-inputs-container-wide">
                    {state.values.map((_, i) => (
                      <SliderNumberField
                        key={i}
                        index={i}
                        aria-labelledby={isRangeSlider ? (i === 0 ? rangeStartLabelId : rangeEndLabelId) : undefined}
                        style={{ order: isRangeSlider ? (i === 0 ? -1 : 1) : undefined }}
                        formatOptions={props.formatOptions}
                      />
                    ))}
                  </div>
                )}
              </div>

              {withInput && (
                <div className="cim-slider-inputs-container-narrow">
                  {state.values.map((_, i) => (
                    <SliderNumberField
                      key={i}
                      index={i}
                      aria-labelledby={isRangeSlider ? (i === 0 ? rangeStartLabelId : rangeEndLabelId) : undefined}
                      formatOptions={props.formatOptions}
                    />
                  ))}
                </div>
              )}

              <FormFieldDescription>{description}</FormFieldDescription>

              {isRangeSlider && (
                <>
                  <span style={{ display: 'none' }} id={rangeStartLabelId}>
                    {messages.format('rangeStart')}
                  </span>
                  <span style={{ display: 'none' }} id={rangeEndLabelId}>
                    {messages.format('rangeEnd')}
                  </span>
                </>
              )}
            </>
          );
        }}
      </RACSlider>
    </RACProvider>
  );
}

/**
 * Allows users to select one or more values within a range.
 *
 * See [slider usage guidelines](https://ui.cimpress.io/components/slider/).
 */
const _Slider = withStyleProps(forwardRef(Slider), 'Slider');

export { _Slider as Slider };

interface SliderNumberFieldProps extends RACNumberFieldProps {
  index: number;
}

function SliderNumberField({ index, 'aria-labelledby': ariaLabelledBy, ...props }: SliderNumberFieldProps) {
  const state = useContext(RACSliderStateContext)!;
  const labelProps = useSlottedContext(RACLabelContext)!;

  return (
    <RACNumberField
      {...props}
      className="cim-slider-input"
      value={state.values[index]}
      onChange={(value) => {
        if (!Number.isNaN(value)) {
          state.setThumbValue(index, value ?? 0);
        }
      }}
      aria-labelledby={[labelProps.id, ariaLabelledBy].filter(Boolean).join(' ')}
    >
      <Input contentWidth="5ch" />
    </RACNumberField>
  );
}
